import * as React from "react";
import { useTranslation } from "react-i18next";

/**
 * FRONTIER WORK — the same board, surface and co-pilot, pointed at our own build. The
 * band sits on the page's light ground and is set apart by its bronze rule, its grid
 * wash and its warm glow, not by going dark; every element inside is tuned for paper.
 *
 * <p>Four slides — the board, the design surface, the co-pilot session, the loop rail —
 * in a carousel that advances every 3.4s and pauses on hover, on keyboard focus inside
 * it, and whenever the tab is hidden; a manual move restarts the clock rather than
 * racing it. Under prefers-reduced-motion it never advances on its own.
 *
 * <p>The carousel's class names are fw-prefixed on purpose: the SchedaAd in the hero
 * owns `.landing .car`, and a bare `.car` here would restyle the advert's photograph
 * stack from half a page away.
 *
 * <p>The mock internals (board cards, trace lines, tile labels) stay in English: they
 * depict the product's own UI, like the chat mock on the Scheda landing. The reader-
 * facing rails, changes and note are translated (frontier.* in the locale bundles).
 */
export function FrontierWork() {
  const { t } = useTranslation();
  const [index, setIndex] = React.useState(0);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const timer = React.useRef<number | null>(null);
  const indexRef = React.useRef(0);
  indexRef.current = index;
  const COUNT = 4;

  const go = React.useCallback((n: number) => setIndex(((n % COUNT) + COUNT) % COUNT), []);
  const stop = React.useCallback(() => {
    if (timer.current !== null) {
      window.clearInterval(timer.current);
      timer.current = null;
    }
  }, []);
  const start = React.useCallback(() => {
    stop();
    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    timer.current = window.setInterval(() => go(indexRef.current + 1), 3400);
  }, [go, stop]);

  React.useEffect(() => {
    start();
    const onVisibility = (): void => {
      if (document.hidden) stop();
      else start();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [start, stop]);

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "ArrowRight") { e.preventDefault(); go(index + 1); start(); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); go(index - 1); start(); }
    else if (e.key === "Home") { e.preventDefault(); go(0); start(); }
    else if (e.key === "End") { e.preventDefault(); go(COUNT - 1); start(); }
  };

  const slideClass = (k: number): string => (k === index ? "fwslide on" : "fwslide");
  const dots = [
    t("frontier.slides.board.dot"),
    t("frontier.slides.surface.dot"),
    t("frontier.slides.copilot.dot"),
    t("frontier.slides.loop.dot"),
  ];

  return (
    <section className="harness" id="own">
      <div className="wrap">
        <div className="hhead">
          <div>
            <p className="eyebrow">{t("frontier.eyebrow")}</p>
            <h2 style={{ marginTop: "12px" }}>{t("frontier.title")}</h2>
          </div>
        </div>

        <div
          className="fwcar"
          ref={rootRef}
          role="group"
          aria-label={t("frontier.ariaGroup")}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onMouseEnter={stop}
          onMouseLeave={start}
          onFocus={stop}
          onBlur={(e) => {
            if (!rootRef.current?.contains(e.relatedTarget as Node)) start();
          }}
        >
          <div className="fwviewport">
            {/* 1. the board */}
            <section className={slideClass(0)} aria-label={t("frontier.slides.board.title")} aria-hidden={index === 0 ? "false" : "true"}>
              <div className="piece">
                <div className="ph">
                  <h3>{t("frontier.slides.board.title")}</h3>
                </div>
                <div className="art">
                  <div className="board">
                    <div className="bcol">
                      <div className="ch"><span>Backlog</span><u>7</u></div>
                      <div className="bcard">
                        <div className="t">Retention sweep for old sessions</div>
                        <span className="lbl blue">Scoped</span>
                      </div>
                      <div className="bcard"><div className="t">Second calendar on the paid tier</div></div>
                      <div className="bcard"><div className="t">Conferencing links on the invite</div></div>
                      <div className="bcard"><div className="t">Poll result announced to everyone asked</div></div>
                    </div>
                    <div className="bcol">
                      <div className="ch"><span>Ready</span><u>2</u></div>
                      <div className="bcard">
                        <div className="t">Reply-to on booking mail</div>
                        <div className="who"><i className="me">Y</i> you ordered this</div>
                      </div>
                      <div className="bcard">
                        <div className="t">Host cancels a booking</div>
                        <div className="who"><i className="me">Y</i> you ordered this</div>
                      </div>
                    </div>
                    <div className="bcol">
                      <div className="ch"><span>Building</span><u>1</u></div>
                      <div className="bcard active">
                        <div className="t">Timezone on the invitee page</div>
                        <div className="who"><i className="ag">A</i> an agent has it</div>
                        <div className="prog"><u style={{ width: "62%" }} /></div>
                      </div>
                      <div className="bcard">
                        <div className="t">Embed on a third-party page</div>
                        <span className="lbl blue">Next up</span>
                      </div>
                    </div>
                    <div className="bcol">
                      <div className="ch"><span>Review</span><u>3</u></div>
                      <div className="bcard">
                        <div className="t">Slot grid no longer re-anchors</div>
                        <span className="lbl coral">Waiting on you</span>
                      </div>
                      <div className="bcard">
                        <div className="t">Outbox retry limit</div>
                        <span className="lbl red">Blocked</span>
                      </div>
                      <div className="bcard">
                        <div className="t">Welcome mail on first sign-in</div>
                        <div className="who"><i className="ag">A</i> an agent prepared it</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 2. the design surface */}
            <section className={slideClass(1)} aria-label={t("frontier.slides.surface.title")} aria-hidden={index === 1 ? "false" : "true"}>
              <div className="piece">
                <div className="ph">
                  <h3>{t("frontier.slides.surface.title")}</h3>
                  <p>{t("frontier.slides.surface.copy")}</p>
                </div>
                <div className="art">
                  <div className="surface">
                    <div className="stoolbar">
                      <span className="z on">Fit</span>
                      <span className="z">100%</span>
                      <span className="z">Compare</span>
                      <span className="sp">4 items on the canvas</span>
                    </div>
                    <div className="tiles">
                      <div className="tile sel">
                        <div className="tt">Option A</div>
                        <div className="frames"><i className="on" /><i /><i /></div>
                      </div>
                      <div className="tile">
                        <div className="tt">Flow</div>
                        <div className="nodes">
                          <u style={{ left: "16px", top: "12px", width: "40px" }} />
                          <u style={{ left: "16px", top: "12px", width: "44px", transform: "rotate(38deg)" }} />
                          <u style={{ left: "60px", top: "12px", width: "34px", transform: "rotate(-32deg)" }} />
                          <i style={{ left: "10px", top: "6px", background: "hsl(var(--brand-blue))" }} />
                          <i style={{ left: "52px", top: "6px", background: "hsl(var(--brand-teal))" }} />
                          <i style={{ left: "90px", top: "6px", background: "hsl(var(--brand-coral))" }} />
                          <i style={{ left: "52px", top: "36px", background: "hsl(var(--brand-moss))" }} />
                        </div>
                      </div>
                      <div className="tile">
                        <div className="tt">Page</div>
                        <div className="skel"><u className="w1" /><u className="w3" /><u className="w2" /><u className="w4" /></div>
                      </div>
                      <div className="tile">
                        <div className="tt">Palette</div>
                        <div className="swatches">
                          <i style={{ background: "hsl(var(--brand-blue))" }} />
                          <i style={{ background: "hsl(var(--brand-teal))" }} />
                          <i style={{ background: "hsl(var(--brand-coral))" }} />
                          <i style={{ background: "hsl(var(--brand-yellow))" }} />
                          <i style={{ background: "var(--brand-metal)" }} />
                          <i style={{ background: "hsl(var(--brand-moss))" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 3. the co-pilot */}
            <section className={slideClass(2)} aria-label={t("frontier.slides.copilot.title")} aria-hidden={index === 2 ? "false" : "true"}>
              <div className="piece">
                <div className="ph">
                  <h3>{t("frontier.slides.copilot.title")}</h3>
                  <p>{t("frontier.slides.copilot.copy")}</p>
                </div>
                <div className="art">
                  <div className="session">
                    <div className="sh"><i /><span>On card: timezone on the invitee page</span></div>
                    <div className="ask">Show the visitor's own timezone, and keep the slot labels honest when they change it.</div>
                    <ul className="trace">
                      <li><em>read</em><b>the card and its two comments</b><span className="ok">✓</span></li>
                      <li><em>found</em><b>4 places that format a slot</b><span className="ok">✓</span></li>
                      <li><em>changed</em><b>3 files, 1 test added</b><span className="ok">✓</span></li>
                      <li><em>ran</em><b>the suite, all green</b><span className="ok">✓</span></li>
                      <li><em>asked</em><b>abbreviation or full name?</b><span className="ok ask2">?</span></li>
                    </ul>
                    <div className="outcome">
                      <b>Ready for review.</b> Card moved, and the question above is waiting on it.
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 4. the loop */}
            <section className={slideClass(3)} aria-label={t("frontier.slides.loop.title")} aria-hidden={index === 3 ? "false" : "true"}>
              <div className="piece">
                <div className="ph">
                  <h3>{t("frontier.slides.loop.title")}</h3>
                  <p>{t("frontier.slides.loop.copy")}</p>
                </div>
                <div className="art">
                  <div className="rail">
                    {([
                      ["r1", true],
                      ["r2", false],
                      ["r3", false],
                      ["r4", false],
                      ["r5", true],
                    ] as const).map(([key, yours], i) => (
                      <div className={yours ? "rstep k" : "rstep"} key={key}>
                        <div className="n">{i + 1}</div>
                        <h4>{t(`frontier.rail.${key}.title`)}</h4>
                        <p>{t(`frontier.rail.${key}.body`)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="fwnav">
            <button className="fwbtn" type="button" aria-label={t("frontier.prev")} onClick={() => { go(index - 1); start(); }}>
              ‹
            </button>
            <div className="fwdots">
              {dots.map((label, k) => (
                <button
                  key={label}
                  type="button"
                  className={k === index ? "fwdot on" : "fwdot"}
                  aria-current={k === index ? "true" : "false"}
                  onClick={() => { go(k); start(); }}
                >
                  {label}
                </button>
              ))}
            </div>
            <button className="fwbtn" type="button" aria-label={t("frontier.next")} onClick={() => { go(index + 1); start(); }}>
              ›
            </button>
            <span className="fwcount">{t("frontier.count", { current: index + 1, total: COUNT })}</span>
          </div>
        </div>

        <div className="changes">
          {(["c1", "c2", "c3", "c4"] as const).map((key) => (
            <div className="change" key={key}>
              <h4>{t(`frontier.changes.${key}.title`)}</h4>
              <p>{t(`frontier.changes.${key}.body`)}</p>
            </div>
          ))}
        </div>

        <p className="harnessnote">{t("frontier.note")}</p>
      </div>
    </section>
  );
}
